// "Pick from block library" picker of the two Add Block dialogs. A click on a row fills the dialog form with
// the content of the library entry and gives the entry id to the dialog (it sends `templateId`).
//
// The list opens when the pointer is on the button. It does not take the focus, so the user can
// type in the description at the same time. A click on the button also puts the cursor in the
// search box. The list stays open while the pointer is on the button, the list, or a row detail,
// and while the search box has the focus. It closes CLOSE_DELAY_MS after the pointer leaves all of
// them. The row detail is a portal inside the list, so the pointer can leave the list DOM and
// stay in the React tree of the list. The list stays open on "mouseover" (it bubbles in the
// React tree), and the detail starts the close timer itself when the pointer leaves it.
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Button, Input, Modal, Popover, Tooltip } from "antd";
import type { FormInstance, InputRef } from "antd";
import { BookOutlined, DownOutlined } from "@ant-design/icons";
import { blockEntryMatches, getBlockEntryCreator, summarizeBlockContent } from "@utils/rtp_utils";
import { useRtpMetricOptions } from "./rtpHooks";
import { BlockContentView } from "./BlockContentView";
import { blockContentToFormValues, buildBlockContent, hasBlockContent } from "./BlockForm";
import type { BlockFormValues } from "./BlockForm";
import type { RtpBlockTemplate } from "./types";

const CLOSE_DELAY_MS = 250;
const DETAIL_CLOSE_DELAY_MS = 150;
const POPUP_CLASS = "rtp-wz rtp-lib-pop";

interface BlockLibraryPickerProps {
	templates: RtpBlockTemplate[];
	// The Add Block dialog form has more fields (days) than the block fields the picker reads.
	form: Pick<FormInstance<BlockFormValues>, "getFieldsValue" | "setFieldsValue">;
	// The dialog keeps the id of the picked entry. A second pick gives a new id.
	onPick: (templateId: string) => void;
	// Icon and caret only, with a tooltip.
	compact?: boolean;
}

export const BlockLibraryPicker = ({ templates, form, onPick, compact = false }: BlockLibraryPickerProps) => {
	const { byKey } = useRtpMetricOptions();
	const metricLabel = (key: string) => byKey[key]?.label ?? key;

	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [detailId, setDetailId] = useState<string | null>(null);
	const searchRef = useRef<InputRef | null>(null);
	const focusPending = useRef(false);
	const pointerInside = useRef(false);
	const searchFocused = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
	const detailTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(
		() => () => {
			clearTimeout(closeTimer.current);
			clearTimeout(detailTimer.current);
		},
		[]
	);

	// The list is gone, so its search box cannot send a blur event.
	const close = () => {
		clearTimeout(closeTimer.current);
		pointerInside.current = false;
		searchFocused.current = false;
		focusPending.current = false;
		setOpen(false);
		setQuery("");
		setDetailId(null);
	};

	const hold = () => {
		pointerInside.current = true;
		clearTimeout(closeTimer.current);
	};

	// Only the button opens the list. The list in its close animation must not open again.
	const openList = () => {
		hold();
		setOpen(true);
	};

	const scheduleClose = () => {
		pointerInside.current = false;
		clearTimeout(closeTimer.current);
		closeTimer.current = setTimeout(() => {
			if (!searchFocused.current) close();
		}, CLOSE_DELAY_MS);
	};

	// The search box is not in the page one task after the open, so the focus waits for it.
	const focusSearch = () => setTimeout(() => searchRef.current?.focus({ preventScroll: true }));
	const setSearchRef = (el: InputRef | null) => {
		searchRef.current = el;
		if (el && focusPending.current) {
			focusPending.current = false;
			focusSearch();
		}
	};

	const handleButtonClick = () => {
		openList();
		if (searchRef.current) focusSearch();
		else focusPending.current = true;
	};

	const showDetail = (id: string) => {
		clearTimeout(detailTimer.current);
		setDetailId(id);
	};

	const hideDetailSoon = () => {
		clearTimeout(detailTimer.current);
		detailTimer.current = setTimeout(() => setDetailId(null), DETAIL_CLOSE_DELAY_MS);
	};

	const fill = (entry: RtpBlockTemplate) => {
		form.setFieldsValue(blockContentToFormValues(entry.content));
		onPick(entry.id);
	};

	const pick = (entry: RtpBlockTemplate) => {
		close();
		if (!hasBlockContent(buildBlockContent(form.getFieldsValue()))) {
			fill(entry);
			return;
		}
		Modal.confirm({
			title: "Replace what you typed?",
			content: `“${entry.title}” replaces the description, exercises, and targets.`,
			okText: "Replace",
			okButtonProps: { "data-attr": "settings.rtp-block-library.picker-replace-submit" } as any,
			onOk: () => fill(entry),
		});
	};

	// The keys typed in the list must not reach the dialog behind it: Esc closes only the list.
	const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Escape") close();
		e.stopPropagation();
	};

	if (templates.length === 0) return null;

	const rows = templates.filter((entry) => blockEntryMatches(entry, query, metricLabel));

	const panel = (
		<div className="rtp-lib-panel" onMouseOver={hold} onMouseLeave={scheduleClose} onKeyDown={handlePanelKeyDown}>
			<Input
				ref={setSearchRef}
				className="rtp-lib-search"
				placeholder="Search the library"
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setDetailId(null);
				}}
				onFocus={() => {
					searchFocused.current = true;
					clearTimeout(closeTimer.current);
				}}
				onBlur={() => {
					searchFocused.current = false;
					if (!pointerInside.current) scheduleClose();
				}}
			/>
			<div className="rtp-lib-rows">
				{rows.length === 0 ? <div className="rtp-lib-empty">No blocks match your search.</div> : null}
				{rows.map((entry) => (
					<Popover
						key={entry.id}
						open={detailId === entry.id}
						trigger={[]}
						placement="left"
						arrow={false}
						destroyOnHidden
						classNames={{ root: `${POPUP_CLASS} rtp-lib-detail-pop` }}
						content={
							<div
								className="rtp-lib-detail"
								onMouseEnter={() => showDetail(entry.id)}
								onMouseLeave={() => {
									hideDetailSoon();
									scheduleClose();
								}}
							>
								<div className="rtp-lib-detail-title">{entry.title}</div>
								<div className="rtp-lib-detail-meta">Saved by {getBlockEntryCreator(entry)}</div>
								<BlockContentView content={entry.content} metricLabel={metricLabel} />
							</div>
						}
					>
						<div
							role="button"
							tabIndex={0}
							className={`rtp-lib-row${detailId === entry.id ? " rtp-lib-row--sel" : ""}`}
							onMouseEnter={() => showDetail(entry.id)}
							onMouseLeave={hideDetailSoon}
							onClick={() => pick(entry)}
							onKeyDown={(e) => {
								if (e.key === "Enter") pick(entry);
							}}
						>
							<div className="rtp-lib-name">{entry.title}</div>
							<div className="rtp-lib-meta">
								{summarizeBlockContent(entry.content)} · {getBlockEntryCreator(entry)}
							</div>
						</div>
					</Popover>
				))}
			</div>
		</div>
	);

	const button = (
		<Button
			size="small"
			className="rtp-lib-btn"
			icon={<BookOutlined />}
			aria-label="Pick from block library"
			data-attr="settings.rtp-block-library.picker-open"
			onMouseEnter={openList}
			onMouseLeave={scheduleClose}
			onClick={handleButtonClick}
		>
			{compact ? null : "Pick from block library"}
			<DownOutlined className="rtp-lib-caret" />
		</Button>
	);

	return (
		<Popover
			open={open}
			trigger={[]}
			placement="bottomLeft"
			arrow={false}
			destroyOnHidden
			classNames={{ root: POPUP_CLASS }}
			content={panel}
		>
			{compact ? <Tooltip title="Pick from block library">{button}</Tooltip> : button}
		</Popover>
	);
};
